/**
 * Coming soon: a storefront that is switched on but not open yet. Its domains serve one page (the
 * store's name, logo, theme and social links, and a few words of the tenant's) and nothing else,
 * until an editor sets it live. The storefront BFF is the gate: it answers that page's config and
 * refuses every other route, so no shopper signs in, reads a box or opens one
 * (docs/storefront.md, "Coming soon").
 *
 * Stored as two columns beside `storefront_configs.enabled` (`coming_soon`, `coming_soon_copy`),
 * so the switch keeps its meaning: off still 404s, whatever `coming_soon` says.
 */
import { z } from "zod";

/** What a storefront's domains serve, in the dashboard control's order. */
export const STOREFRONT_SERVING_STATES = ["off", "coming_soon", "live"] as const;
export type StorefrontServingState = (typeof STOREFRONT_SERVING_STATES)[number];

/**
 * The one reading of the two switches: `enabled` off is off (the config 404s) whatever
 * `comingSoon` says, so a store taken off while coming soon comes back coming soon.
 */
export function storefrontServingState(switches: {
  enabled: boolean;
  comingSoon: boolean;
}): StorefrontServingState {
  if (!switches.enabled) return "off";
  return switches.comingSoon ? "coming_soon" : "live";
}

/**
 * The switches a state is saved as. Off writes `enabled` alone and leaves `comingSoon` as it
 * was, so a store that goes off while coming soon is never live when it is switched back on.
 */
export function storefrontServingSwitches(
  state: StorefrontServingState,
): { enabled: false } | { enabled: true; comingSoon: boolean } {
  if (state === "off") return { enabled: false };
  return { enabled: true, comingSoon: state === "coming_soon" };
}

/** The longest each of the page's words may be, in characters. */
export const COMING_SOON_COPY_LIMITS = { headline: 80, message: 280, note: 80 } as const;

/**
 * The tenant's words for the page, each optional: a field left out shows the default copy
 * (`defaultComingSoonCopy`). The stored shape is the wire shape (`{}` = every default); text only,
 * rendered as text.
 */
export const storefrontComingSoonCopySchema = z.object({
  /** The page's h1. */
  headline: z.string().trim().max(COMING_SOON_COPY_LIMITS.headline).optional(),
  /** A sentence or two under it. */
  message: z.string().trim().max(COMING_SOON_COPY_LIMITS.message).optional(),
  /** One short line under the message ("Opening December 2026"); no default. */
  note: z.string().trim().max(COMING_SOON_COPY_LIMITS.note).optional(),
});
export type StorefrontComingSoonCopy = z.infer<typeof storefrontComingSoonCopySchema>;

/** The copy as stored: blank fields dropped, so a blank field shows the default copy. */
export function compactComingSoonCopy(copy: StorefrontComingSoonCopy): StorefrontComingSoonCopy {
  const out: StorefrontComingSoonCopy = {};
  if (copy.headline) out.headline = copy.headline;
  if (copy.message) out.message = copy.message;
  if (copy.note) out.note = copy.note;
  return out;
}

/** On the resolved config while the storefront is coming soon; null once it is live. */
export const storefrontComingSoonSchema = z.object({ copy: storefrontComingSoonCopySchema });
export type StorefrontComingSoon = z.infer<typeof storefrontComingSoonSchema>;
